// AiSettingsScreen — director-only window to tune the per-stage AI logic
// (what each stage means, the move triggers, the manager script) and flip the
// auto-execute switch. Backed by /api/miniapp/ai-settings; the values feed the
// AI's call/message analysis via render_stage_block on the server.
import type React from 'react'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Button, Icon, SectionHeader, ScreenSkeleton, Toggle, useToast, t } from '../../design'
import {
  type AiProfile,
  type AiSettingsStage,
  type AiTransition,
  type StagePayload,
  fetchAiSettings,
  saveAiProfile,
  saveStageLogic,
  setAutoExecute,
} from '../../api/aiSettings'

interface StageRef { pipeline: string; stage: AiSettingsStage }
interface StageOption { value: string; label: string }

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 11px', borderRadius: 10, font: '400 14px/1.4 var(--font-sans)',
  border: '1px solid var(--border)', background: 'var(--surface-1)', color: 'var(--text-1)',
  boxSizing: 'border-box', resize: 'vertical',
}
const labelStyle: React.CSSProperties = { font: '600 12px/1.3 var(--font-sans)', color: 'var(--text-2)', marginBottom: 4, display: 'block' }

export function AiSettingsScreen() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useQuery({ queryKey: ['ai-settings'], queryFn: fetchAiSettings })
  const [editing, setEditing] = useState<StageRef | null>(null)

  const autoExecMut = useMutation({
    mutationFn: (enabled: boolean) => setAutoExecute(enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-settings'] }),
    onError: () => toast(t('ai.set.saveErr')),
  })

  // Flat option list for transition targets: every stage across all funnels.
  const stageOptions: StageOption[] = useMemo(() => {
    const out: StageOption[] = []
    for (const p of data?.pipelines ?? []) {
      for (const s of p.stages) {
        out.push({ value: `${p.pipeline_ext_id} ${s.ext_id}`, label: `${p.name} · ${s.name}` })
      }
    }
    return out
  }, [data])

  if (isLoading) return <ScreenSkeleton />

  if (editing) {
    return (
      <StageEditor
        key={`${editing.pipeline}:${editing.stage.ext_id}`}
        pipeline={editing.pipeline}
        stage={editing.stage}
        stageOptions={stageOptions}
        onClose={() => setEditing(null)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['ai-settings'] }); setEditing(null); toast(t('ai.set.saved')) }}
      />
    )
  }

  const auto = !!data?.auto_execute_moves
  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 32px' }}>
      <div className="t-h1" style={{ marginBottom: 4 }}>{t('ai.set.title')}</div>
      <div className="t-body-s dim" style={{ marginBottom: 16 }}>{t('ai.set.intro')}</div>

      {/* Auto-execute master switch */}
      <div className="card card-16" style={{ marginBottom: 20 }}>
        <div className="row middle between" style={{ gap: 12 }}>
          <div className="flex-1" style={{ minWidth: 0 }}>
            <div className="t-body-s" style={{ fontWeight: 600 }}>{t('ai.set.autoExec')}</div>
            <div className="t-caption">{auto ? t('ai.set.autoExecOn') : t('ai.set.autoExecOff')}</div>
          </div>
          <Toggle value={auto} onChange={(next: boolean) => { if (!autoExecMut.isPending) autoExecMut.mutate(next) }} />
        </div>
        <div className="t-caption dim" style={{ marginTop: 8 }}>{t('ai.set.autoExecHint')}</div>
      </div>

      <AiProfileSection initial={data?.ai_profile ?? {}} />

      {(data?.pipelines ?? []).map((p) => (
        <div key={p.pipeline_ext_id} style={{ marginBottom: 8 }}>
          <SectionHeader>{p.name}</SectionHeader>
          <div className="card" style={{ padding: 0, marginBottom: 8 }}>
            {p.stages.map((s, i) => {
              const configured = !!(s.settings.context || s.settings.transitions.length || s.settings.script?.steps?.length)
              return (
                <button
                  key={s.ext_id}
                  onClick={() => setEditing({ pipeline: p.pipeline_ext_id, stage: s })}
                  className="row middle gap-3"
                  style={{
                    width: '100%', padding: '13px 16px', border: 'none', background: 'transparent',
                    cursor: 'pointer', textAlign: 'left',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <div className="flex-1" style={{ minWidth: 0 }}>
                    <div className="t-body-s">{s.name}</div>
                    <div className="t-caption" style={{ color: configured ? 'var(--brand-700)' : 'var(--text-3)' }}>
                      {configured ? t('ai.set.stageConfigured') : t('ai.set.stageEmpty')}
                    </div>
                  </div>
                  <Icon.ArrowRight size={18} sw={2} style={{ color: 'var(--text-3)' }} />
                </button>
              )
            })}
          </div>
        </div>
      ))}

      {(data?.pipelines ?? []).length === 0 && (
        <div className="t-body-s dim" style={{ padding: 16, textAlign: 'center' }}>{t('ai.set.empty')}</div>
      )}
    </div>
  )
}

// ── AI profile (tenant-wide voice & standards) ──────────────────────────────

function toLines(s: string): string[] {
  return s.split('\n').map((x) => x.trim()).filter(Boolean)
}

function AiProfileSection({ initial }: { initial: AiProfile }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [business, setBusiness] = useState(initial.business ?? '')
  const [tone, setTone] = useState(initial.tone ?? '')
  const [lang, setLang] = useState(initial.reply_language ?? '')
  const [glossary, setGlossary] = useState((initial.glossary ?? []).join('\n'))
  const [priorities, setPriorities] = useState((initial.priorities ?? []).join('\n'))
  const [forbidden, setForbidden] = useState((initial.forbidden ?? []).join('\n'))

  const mut = useMutation({
    mutationFn: () => saveAiProfile({
      business, tone, reply_language: lang,
      glossary: toLines(glossary), priorities: toLines(priorities), forbidden: toLines(forbidden),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-settings'] }); toast(t('ai.set.saved')) },
    onError: () => toast(t('ai.set.saveErr')),
  })

  return (
    <div style={{ marginBottom: 20 }}>
      <SectionHeader>{t('ai.set.profile')}</SectionHeader>
      <div className="card card-16" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="t-caption dim">{t('ai.set.profileHint')}</div>
        <Field label={t('ai.set.business')}>
          <textarea
            style={inputStyle}
            rows={4}
            value={business}
            onChange={(e) => setBusiness(e.target.value)}
            placeholder={t('ai.set.businessPh')}
            maxLength={2000}
          />
        </Field>
        <Field label={t('ai.set.tone')}>
          <input style={inputStyle} value={tone} onChange={(e) => setTone(e.target.value)} placeholder={t('ai.set.tonePh')} />
        </Field>
        <Field label={t('ai.set.replyLang')}>
          <input style={inputStyle} value={lang} onChange={(e) => setLang(e.target.value)} placeholder={t('ai.set.replyLangPh')} />
        </Field>
        <Field label={t('ai.set.glossary')}>
          <textarea style={inputStyle} rows={3} value={glossary} onChange={(e) => setGlossary(e.target.value)} placeholder={t('ai.set.listPh')} />
        </Field>
        <Field label={t('ai.set.priorities')}>
          <textarea style={inputStyle} rows={2} value={priorities} onChange={(e) => setPriorities(e.target.value)} placeholder={t('ai.set.listPh')} />
        </Field>
        <Field label={t('ai.set.forbidden')}>
          <textarea style={inputStyle} rows={2} value={forbidden} onChange={(e) => setForbidden(e.target.value)} placeholder={t('ai.set.listPh')} />
        </Field>
        <Button sm onClick={() => mut.mutate()} disabled={mut.isPending}>
          {mut.isPending ? t('ai.set.saving') : t('ai.set.save')}
        </Button>
      </div>
    </div>
  )
}

// ── per-stage editor ────────────────────────────────────────────────────────

function StageEditor({
  pipeline, stage, stageOptions, onClose, onSaved,
}: {
  pipeline: string
  stage: AiSettingsStage
  stageOptions: StageOption[]
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const s = stage.settings
  const [purpose, setPurpose] = useState(s.context?.purpose ?? '')
  const [entry, setEntry] = useState(s.context?.entry_conditions ?? '')
  const [exit, setExit] = useState(s.context?.exit_conditions ?? '')
  const [steps, setSteps] = useState(s.script?.steps ?? [])
  const [transitions, setTransitions] = useState<AiTransition[]>(s.transitions ?? [])

  const saveMut = useMutation({
    mutationFn: (payload: StagePayload) => saveStageLogic(pipeline, stage.ext_id, payload),
    onSuccess: onSaved,
    onError: () => toast(t('ai.set.saveErr')),
  })

  function save() {
    const payload: StagePayload = {
      context: (purpose || entry || exit)
        ? { purpose, entry_conditions: entry, exit_conditions: exit }
        : null,
      script: steps.length ? { steps } : null,
      transitions,
      stage_tasks: stage.settings.stage_tasks ?? [],
    }
    saveMut.mutate(payload)
  }

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 96px' }}>
      <button onClick={onClose} className="row middle" style={{ gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, marginBottom: 10, color: 'var(--brand-700)' }}>
        <Icon.ArrowRight size={16} sw={2} style={{ transform: 'rotate(180deg)' }} />
        <span className="t-body-s">{t('ai.set.back')}</span>
      </button>
      <div className="t-h1" style={{ marginBottom: 16 }}>{stage.name}</div>

      {/* Context */}
      <SectionHeader>{t('ai.set.ctx')}</SectionHeader>
      <div className="card card-16" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label={t('ai.set.ctxPurpose')}>
          <textarea style={inputStyle} rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder={t('ai.set.ctxPurposePh')} />
        </Field>
        <Field label={t('ai.set.ctxEntry')}>
          <textarea style={inputStyle} rows={2} value={entry} onChange={(e) => setEntry(e.target.value)} />
        </Field>
        <Field label={t('ai.set.ctxExit')}>
          <textarea style={inputStyle} rows={2} value={exit} onChange={(e) => setExit(e.target.value)} />
        </Field>
      </div>

      {/* Transitions */}
      <SectionHeader>{t('ai.set.transitions')}</SectionHeader>
      <div className="card card-16" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="t-caption dim">{t('ai.set.transitionsHint')}</div>
        {transitions.map((tr, i) => {
          const value = `${tr.to_pipeline} ${tr.to_stage}`
          return (
            <div key={i} className="card-16" style={{ background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Field label={t('ai.set.trigger')}>
                <input style={inputStyle} value={tr.trigger} onChange={(e) => updateTransition(setTransitions, i, { trigger: e.target.value })} placeholder={t('ai.set.triggerPh')} />
              </Field>
              <Field label={t('ai.set.target')}>
                <select style={inputStyle} value={value} onChange={(e) => {
                  const [tp, ts] = e.target.value.split(' ')
                  updateTransition(setTransitions, i, { to_pipeline: tp, to_stage: ts })
                }}>
                  <option value=" " disabled>—</option>
                  {stageOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <button onClick={() => setTransitions((xs) => xs.filter((_, j) => j !== i))}
                      style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', font: '600 13px/1 var(--font-sans)', padding: '2px 0' }}>
                {t('ai.set.remove')}
              </button>
            </div>
          )
        })}
        <Button variant="secondary" sm icon={<Icon.Plus size={15} sw={2} />}
                onClick={() => setTransitions((xs) => [...xs, { to_pipeline: '', to_stage: '', trigger: '', priority: xs.length + 1 }])}>
          {t('ai.set.addTransition')}
        </Button>
      </div>

      {/* Script */}
      <SectionHeader>{t('ai.set.script')}</SectionHeader>
      <div className="card card-16" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="t-caption dim">{t('ai.set.scriptHint')}</div>
        {steps.map((st, i) => (
          <div key={i} className="card-16" style={{ background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Field label={t('ai.set.stepTitle')}>
              <input style={inputStyle} value={st.title} onChange={(e) => updateStep(setSteps, i, { title: e.target.value })} />
            </Field>
            <Field label={t('ai.set.stepDesc')}>
              <input style={inputStyle} value={st.description ?? ''} onChange={(e) => updateStep(setSteps, i, { description: e.target.value })} />
            </Field>
            <label className="row middle" style={{ gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!st.required} onChange={(e) => updateStep(setSteps, i, { required: e.target.checked })} />
              <span className="t-caption">{t('ai.set.stepRequired')}</span>
            </label>
            <button onClick={() => setSteps((xs) => xs.filter((_, j) => j !== i))}
                    style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', font: '600 13px/1 var(--font-sans)', padding: '2px 0' }}>
              {t('ai.set.remove')}
            </button>
          </div>
        ))}
        <Button variant="secondary" sm icon={<Icon.Plus size={15} sw={2} />}
                onClick={() => setSteps((xs) => [...xs, { title: '', required: false, description: '' }])}>
          {t('ai.set.addStep')}
        </Button>
      </div>

      {/* Sticky save */}
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, padding: '12px 16px', background: 'var(--surface-1)', borderTop: '1px solid var(--border)', maxWidth: 420, margin: '0 auto' }}>
        <Button full onClick={save} disabled={saveMut.isPending}>
          {saveMut.isPending ? t('ai.set.saving') : t('ai.set.save')}
        </Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span style={labelStyle}>{label}</span>
      {children}
    </div>
  )
}

type SetSteps = React.Dispatch<React.SetStateAction<{ n?: number; title: string; required: boolean; description?: string | null }[]>>
function updateStep(setSteps: SetSteps, i: number, patch: Partial<{ title: string; required: boolean; description: string | null }>) {
  setSteps((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)))
}

type SetTransitions = React.Dispatch<React.SetStateAction<AiTransition[]>>
function updateTransition(setTransitions: SetTransitions, i: number, patch: Partial<AiTransition>) {
  setTransitions((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)))
}
